import type { FoodItem as NutritionFoodItem } from "@/packages/dashboard/api/get-meals";

import { Button, Group, Loader, Stack, Text } from "@mantine/core";
import { useState } from "react";

import { nuaInferNutritionInfo } from "./nua-infer-nutrition-info";

type InferNutritionButtonProps = {
  foodName: string;
  onApply: (data: NutritionFoodItem) => void;
  disabled?: boolean;
};

export const InferNutritionButton = ({ foodName, onApply, disabled = false }: InferNutritionButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (isLoading) {
      return;
    }

    const trimmedName = foodName.trim();

    if (!trimmedName) {
      setError("Enter a food name before inferring.");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await nuaInferNutritionInfo(trimmedName);

      if (result.isError) {
        setError(result.error ?? "Unable to infer nutrition info.");
        return;
      }

      onApply(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to infer nutrition info.");
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled = disabled || isLoading;

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        <Button
          type="button"
          variant="light"
          size="xs"
          onClick={handleClick}
          disabled={isButtonDisabled || !foodName.trim()}
        >
          Infer nutrition info
        </Button>
        {isLoading && <Loader size="xs" />}
      </Group>
      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};
